import Utils from "../utils/utils"
import { Attribute } from "./attribute.model"
import { Colors } from "./colors.model"
import { FeaturedAsset } from "./featuredAsset.model"
import { LearningObject } from "./learningObject.model"
import NavigationNode from "./navigationNode.model"
import ResourceUtils from "../utils/resource-utils"

export class CollectionNode implements NavigationNode {
    public id: number = null
    public publish: boolean = true
    public loId: string = null
    public title: string = ""
    public description: string = ""
    public banner: string = ""
    public slug: string = ""
    public posterImage: string = ""
    public featuredAsset: FeaturedAsset = new FeaturedAsset({})
    public children: CollectionNode[] = []
    public parents: CollectionNode[] = []
    public resourceMediaTypes: { [mediaType: string]: number } = {}
    public analyticsGrades: string[] = []
    public analyticsGradesDisplay: string
    public resourceGrades: string[] = []
    public resourceGradesDisplay: string
    public style: Colors = new Colors({})
    public attributions: any[] = []
    public credits: string = null
    public footer: any = {}
    public resources: LearningObject[][] = []
    public poster_images: string[] = []
    public nextResourcesURI: string = ""
    public isLoadingResources: boolean = false
    // Need to know if the collection has no resources
    public firstResourcesRequested: boolean = false
    public totalResources: number = null
    // When this is true, the user should have not landed on a standard collection and should be
    // redirected to a premium collection homepage
    public isPremium: boolean = false
    public brands: string[] = []
    public contentProjects: string[] = []
    public topLevelSubjects: string[] = []
    public resourcesByLanguage?: { [language: string]: number } = {}
    public redirectUrl?: string

    public constructor(input: object) {
        this.assignToObject(input)
    }

    public assignToObject(input: object): void {
        if (input) {
            Object.keys(input).map((key: string) => {
                const property: string = Utils.convertToCamelcase(key)
                if (key === "style") {
                    this[property] = new Colors(input[key])
                } else {
                    this[property] = input[key]
                }

                if (key === "featured_asset") {
                    this.featuredAsset = new FeaturedAsset(input[key])
                }
            })
        }
        this.posterImage = Utils.escapePosterImage(this.posterImage)
        this.analyticsGradesDisplay = ResourceUtils.getGradesDisplay(this.analyticsGrades)
        this.resourceGradesDisplay = ResourceUtils.getGradesDisplay(this.resourceGrades)
    }

    public getRouterLink(isPreview: boolean = false): string {
        let previewLink: string = isPreview ? "/preview" : ""
        if (this.isPremium && this.slug) {
            return `/collection/${this.slug}/home/${previewLink}`
        }
        if (this.slug) {
            return `/collection/${this.slug}/${previewLink}`
        }
        if (this.parents.length > 0) {
            return `/collection/${this.parents[0].slug}/${previewLink}`
        }
        return "/"
    }

    public hasFeaturedAssets(): boolean {
        return this.featuredAsset.media.length !== 0
    }

    public getFeaturedAssetType(): string {
        if (this.hasFeaturedAssets() && this.featuredAsset.mediaType) {
            return this.featuredAsset.mediaType
        }
    }

    public getFeaturedAssetMedia(): any[] {
        if (this.hasFeaturedAssets() && this.featuredAsset.media) {
            return this.featuredAsset.media
        }
    }

    public getFeaturedAssetTitle(): string {
        if (this.hasFeaturedAssets() && this.featuredAsset.title) {
            return this.featuredAsset.title
        }
    }

    public get mediaTypesDisplay() {
        return ResourceUtils.getMediaTypesDisplay(this.resourceMediaTypes)
    }

    public get resourceByLanguage() {
        return ResourceUtils.getResourcesByLanguage(this.resourcesByLanguage)
    }

    public generateBreadcrumbs(isStudent: boolean = false, isPreview: boolean = false): any[] {
        const breadcrumbs: any[] = [
            {
                label: "Collections",
                link: "/search",
                params: {
                    selected_facet: "media_type:Collection",
                    student: isStudent ? true : null,
                },
            },
        ]
        this.parents.forEach((element: CollectionNode) => {
            breadcrumbs.push({
                label: element.title,
                link: element.getRouterLink(isPreview),
                params: { student: isStudent ? true : null },
            })
        })
        breadcrumbs.push({
            label: this.title,
            link: this.getRouterLink(isPreview),
            params: { student: isStudent ? true : null },
        })
        return breadcrumbs
    }

    public getAllAttributions(): any[] {
        let attributions: any[] = []
        this.parents.forEach((parent: CollectionNode) => {
            attributions = attributions.concat(parent.attributions)
        })
        return attributions.concat(this.attributions)
    }

    public getFounders(): Attribute[] {
        return this.getAllAttributions()
            .map((item) => {
                return new Attribute(item)
            })
            .filter((x: Attribute): boolean => x.role === "funder")
    }

    public getCredits(): string {
        if (this.parents.length > 0) {
            return this.parents[0].credits
        }
        return this.credits
    }

    public getContributors(): Attribute[] {
        return Attribute.fromDataArray(this.getAllAttributions())
    }
}
